import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Input } from '@heroui/input';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { useAuth } from '../../contexts/AuthContext';

const ForgotPassword: React.FC = () => {
  const pageTitle = 'Forgot Password';
  const pageDescription =
    'Request a password reset link to be sent to your email.';
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const validateEmail = (value: string): string => {
    if (!value) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Invalid email format';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }
    setEmailError('');
    setIsLoading(true);
    try {
      await forgotPassword({ email });
      setSubmitted(true);
      toast.success(
        'If an account exists, a password reset link has been sent.'
      );
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || 'Something went wrong. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-8'>
          <div className='text-center'>
            <h1 className='text-3xl font-bold text-gray-900'>
              Forgot your password?
            </h1>
            <p className='mt-2 text-sm text-gray-600'>
              Enter your email and we'll send you a link to reset it.
            </p>
          </div>

          <Card>
            <CardHeader className='pb-2'>
              <h2 className='text-xl font-semibold text-center w-full'>
                Reset Password
              </h2>
            </CardHeader>
            <CardBody>
              {submitted ? (
                <div className='text-center space-y-4'>
                  <p className='text-gray-600'>
                    If an account exists for {email}, a password reset link has
                    been sent. Please check your inbox and follow the link.
                  </p>
                  <p className='text-sm text-gray-500'>
                    The link will expire in 1 hour. If you didn't receive it,
                    check your spam folder or try again.
                  </p>
                  <Button
                    as={Link}
                    to='/login'
                    color='primary'
                    className='w-full'
                  >
                    Back to sign in
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className='space-y-6'>
                  <Input
                    type='email'
                    label='Email address'
                    placeholder='Enter your email'
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      setEmailError('');
                    }}
                    variant='bordered'
                    size='lg'
                    isInvalid={!!emailError}
                    errorMessage={emailError}
                    required
                  />
                  <Button
                    type='submit'
                    color='primary'
                    size='lg'
                    className='w-full'
                    isLoading={isLoading}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Send reset link'}
                  </Button>
                </form>
              )}

              <div className='mt-4 text-center'>
                <Link
                  to='/login'
                  className='text-sm text-primary hover:text-primary-500'
                >
                  Back to sign in
                </Link>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
